/**
 * Saved cart model holds data regarding a saved cart instance
 * Saved carts are part of the shared resource system
 * `fetch` will get you the shared resource bean for the saved cart
 * `fetchBean` will get you the actual saved cart bean
 * @class
 */
import AssetSetModel from '@netx/core-asset/models/assetSet';
import {types as assetSetTypes} from '@netx/core-asset/properties/assetSets';
// Portal can't handle this import - using the global that is needed in other spots as well
// import {cartController} from '@netx/core-cart-next/controller';
import {URL} from '@netx/core-next/url';
import {getObjectIdFromProgressReport, pollUntil} from '@netx/core-utils';
import AssetSearchRulesCollection from '@netx/core-search/collections/searchRules_assets';
import Base from '@netx/core-share/models/sharedResource';
import CartAssetsCollection from '../collections/cartAssets';
import customConfig from '../config';

export default class SavedCart extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/**
	 * @property {object} methods                       - Methods for this model
	 * @property {method} methods.create                - Save new cart
	 * @property {method} methods.read                  - Fetch saved cart
	 * @property {method} methods.remove                - Destroy saved cart
	 * @property {method} methods.update                - Update saved cart
	 *
	 * @property {method} methods.addAssetsToCart       - Add assets to saved cart
	 * @property {method} methods.archiveCartAssets     - Archive assets in saved cart
	 * @property {method} methods.download              - Download cart assets
	 * @property {method} methods.duplicateCart         - Duplicate saved cart
	 * @property {method} methods.expireCartAssets      - Set expiration for assets in saved cart
	 * @property {method} methods.fetchBean             - Fetch saved search bean (fetch gets the shared resource)
	 * @property {method} methods.inspect               - Inspect saved cart contents
	 * @property {method} methods.removeAssetsFromCart  - Remove assets from saved cart
	 * @property {method} methods.setCart               - Set cart data
	 * @property {method} methods.unarchiveCartAssets   - Unarchive assets in saved cart
	 */
	get methods() {
		return _.extend(super.methods, {
			/**
			 * Create saved cart
			 * @method
			 * @private
			 */
			create(opts) {
				// TODO: Need the DAM to return full beans on creation
				// in this case (all shared resource cases) it would be the shared resource and not the underlying cart bean itself
				return this.setCart().then((id) => {
					this.set({id: id}, opts);
					return this.fetch(opts);
				});
			},
			/**
			 * Destroy saved cart
			 * @method
			 */
			remove: ['deleteCart', 'id'],
			/**
			 * Update saved cart
			 * @method
			 */
			update(opts) {
				return this.setCart(opts);
				// ['setCart', 'id', 'title'];
			},

			/**
			 * Add assets to saved cart
			 * @method
			 * @param {array} assets Asset ids
			 * @param {object} opts Request options
			 */
			addAssetsToCart(assets, opts) {
				return [
					'addAssetsToCart',
					this.id,
					_.tidyAssetList.apply(this, [assets]),
				];
			},
			/**
			 * Archive cart assets
			 * @method
			 */
			archiveCartAssets: ['archiveCartAssets', 'id', 'archiveArgs.locationId'],
			/**
			 * Download cart assets
			 * @method
			 * @param {object} opts Request options
			 */
			download(opts) {
				return ['downloadAssetsFromSavedCart', this.id, true];
			},
			/**
			 * Duplicate saved cart
			 * @method
			 * @param {string} cartName Saved cart name
			 * @param {object} opts Request options
			 */
			duplicateCart(cartName, opts) {
				return ['duplicateCart', cartName, this.id];
			},
			/**
			 * This works for both expiring and unexpiring
			 * Unexpireing just means setting 0 on expirationDate
			 * @method
			 */
			expireCartAssets: ['expireCartAssets', 'id', 'expirationArgs.date'],
			/**
			 * Fetch saved cart
			 * @method
			 */
			fetchBean: ['getCart', 'id'],
			/**
			 * Report on the contents of this cart.
			 * @method
			 */
			inspect: ['inspectCartContents', 'id'],
			/**
			 * Remove assets from saved cart
			 * @method
			 * @param {array} assets Asset ids
			 * @param {object} opts Request options
			 */
			removeAssetsFromCart(assets, opts) {
				return [
					'removeAssetsFromCart',
					this.id,
					_.tidyAssetList.apply(this, arguments),
				];
			},

			setCart(opts) {
				return this._setCart(opts).then((uuid) =>
					pollUntil({uuid, throwOnWarnings: true}).then((progressReport) =>
						getObjectIdFromProgressReport(progressReport, true),
					),
				);
			},

			/**
			 * Set cart data
			 * @method
			 */
			_setCart(opts) {
				return ['setCart', this.id || 0, this.get('title')];
			},

			/**
			 * Unarchive cart assets
			 * @method
			 */
			unarchiveCartAssets: [
				'unarchiveCartAssets',
				'id',
				'unarchiveArgs.type',
				'unarchiveArgs.email',
			],
		});
	}

	/**
	 * @property {object} parsers                         - Parsers for this model
	 * @property {method} parsers.addAssetsToCart         - Add assets to saved cart
	 * @property {method} parsers.fetchBean               - Parse response of fetch saved cart bean
	 * @property {method} parsers.removeAssetsFromCart    - Remove assets from saved cart
	 * @property {method} parsers.setAssetsInCart         - Replace contents of saved cart
	 */
	get parsers() {
		return _.extend(super.parsers, {
			/**
			 * Add assets to saved cart
			 * @method
			 * @param {number} newCount Saved cart's asset count
			 * @param {object} opts Request options
			 */
			addAssetsToCart(newCount, opts) {
				this.set({contentCount: newCount}, opts);
			},
			// /**
			//  * Add search to saved cart
			//  * @method
			//  * @param {number} newCount Saved cart's asset count
			//  * @param {object} opts Request options
			//  */
			// addFacetedSearchToCart(newCount, opts) {
			// 	this.set({contentCount: newCount}, opts);
			// },
			/**
			 * Remove assets from saved cart
			 * @method
			 * @param {number} newCount Saved cart's asset count
			 * @param {object} opts Request options
			 */
			removeAssetsFromCart(newCount, opts) {
				this.set({contentCount: newCount}, opts);
			},
		});
	}

	/** @type {string} */
	get sharedResourceRoute() {
		return `cart-assets/${this.id}`;
	}

	/**
	 * @override
	 * @param {object} attr Model attributes
	 * @param {object} opts Model options
	 */
	constructor(attrs, opts = {}) {
		/**
		 * Resource type name
		 * @type {string}
		 */
		opts.resourceName = 'savedCart';

		super(attrs, opts);

		/**
		 * @memberof module:cart/models/savedCart
		 * @alias archiveArgs
		 * @property {object} archiveArgs - Arguments to the archive cart assets method
		 * @property {number} archiveArgs.locationId - Archive location id
		 */
		this.archiveArgs = _.extend(
			{
				locationId: 0,
			},
			opts.archiveArgs,
		);

		/**
		 * For adding/removing assets
		 * Array of asset Ids
		 * @memberof module:cart/models/savedCart
		 * @alias assetArg
		 * @type {array}
		 */
		this.assetsArg = opts.assetsArg || [];

		/**
		 * @memberof module:cart/models/savedCart
		 * @alias expirationArgs
		 * @property {object} expirationArgs - Arguments to the expire cart assets method
		 * @property {array}  expirationArgs.date - Expiration date to set
		 */
		this.expirationArgs = _.extend(
			{
				date: 0,
			},
			opts.expirationArgs,
		);

		/**
		 * @memberof module:cart/models/savedCart
		 * @alias unarchiveArgs
		 * @property {object} unarchiveArgs - Arguments to the unarchive cart assets method
		 * @property {number} unarchiveArgs.unarchiveType - Type of unarchive ( 1 = temporary, 2 = permanently )
		 * @property {string} unarchiveArgs.email         - Email address to notify on unarchive complete
		 */
		this.unarchiveArgs = _.extend(
			{
				type: 1,
				email: '',
			},
			opts.unarchiveArgs,
		);

		// Hey, listen!
		this.listenTo(dispatcher, 'cart:rename:success', (model) => {
			model.id === this.id &&
				model !== this &&
				this.set('title', model.get('title'));
		});
	}

	/**
	 * Add current cart assets
	 * @method
	 */
	addCurrentCart() {
		return window.Netx.cartController.addCurrentCartToSavedCart(this.id);
	}

	/**
	 * @method
	 * @param {number} locationId Archive location id
	 */
	archive() {
		return CartAssetsCollection.prototype.archive.apply(this, arguments);
	}

	/**
	 * @method
	 * @param {date|string} date Date
	 */
	expireAssets() {
		return CartAssetsCollection.prototype.expireAssets.apply(this, arguments);
	}

	/**
	 * @method
	 * @return {array} Asset ids
	 */
	getAssetIds() {
		return this.getCartAssets().getAssetIds();
	}

	/**
	 * @method
	 * @return {Netx.Collections.CartAssets} Assets
	 */
	getCartAssets() {
		// TODO: are we making the code that calls this fetch this collection?
		// because we would be retunring an empty collection here
		if (_.isUndefined(this.cartAssets)) {
			this.cartAssets = new CartAssetsCollection([], {id: this.id});
		}
		return this.cartAssets;
	}

	/**
	 * @method
	 * @param {boolean} fullUrl Return full url
	 * @return {string}
	 */
	getRoute(fullUrl) {
		return (
			((fullUrl && URL.location.split('#')[0]) || '') +
			'#' +
			customConfig.ROUTE_SAVED_CART +
			'/' +
			this.id
		);
	}

	/**
	 * Change saved cart name
	 * @method
	 * @param {string} name New saved cart name
	 * @todo Opts?
	 */
	rename(name) {
		this.set({title: name});
		return this.save();
	}

	/**
	 * Get an assetSet model defining the set of this cart's assets
	 * @method
	 * @return {module:asset/models/assetSet} Asset set
	 */
	toAssetSet() {
		return new AssetSetModel({
			type: assetSetTypes.savedCart,
			ids: [this.id],
			models: [this],
			label: this.getLabel(),
		});
	}

	/**
	 * @method
	 * @param {number} type  Unarchive type
	 * @param {string} email Email to notify on completion
	 */
	unarchive(type, email) {
		return CartAssetsCollection.prototype.unarchive.apply(this, arguments);
	}

	/**
	 * @method
	 */
	unexpireAssets() {
		return CartAssetsCollection.prototype.unexpireAssets.apply(this, arguments);
	}

	/**
	 * Create a saved cart from a name and some assets
	 * @method
	 * @param {string}                        name   Saved cart name
	 * @param {Netx.Collections.Assets|array} assets Either a collection of assets or an array of asset ids
	 */
	static Create(name, assets) {
		const sc = new this.constructor({title: name});

		sc.assetsArg = _.tidyAssetList(assets);
		return sc.create().then(() => {
			return sc.fetch().then(() => {
				return sc;
			});
		});
	}

	/**
	 * Load a saved cart from an ID
	 * @method
	 * @param {number} id Saved cart id
	 */
	static Load(id) {
		const sc = new this.constructor({id: id});

		return sc.fetch().then(() => {
			return sc;
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'SavedCart';
	}
}
